/**
 * A canvas over the still, drawing it in blocks (mosaic.ts): the block size
 * follows `progress` from the start size down to the end size, moving
 * smoothly between the polls' values rather than jumping. Cover-fitted like
 * the still under it, so the blocks line up with the picture. Redrawn only
 * when the block size or the box changes.
 */

import React, { useEffect, useRef } from 'react';
import { mosaicBlock } from './mosaic';

export interface MosaicLayerProps {
  src: string | null;
  /** 0 … 1, how near the first picture is (mosaic.ts firstPictureProgress). */
  progress: number;
  /** Block sizes, CSS px, at progress 0 and 1. */
  start: number;
  end: number;
  className?: string;
}

/** Seconds for the shown progress to close most of the gap to a new target (a poll is 3 s). */
const SMOOTH_SEC = 1.4;

export const MosaicLayer: React.FC<MosaicLayerProps> = ({ src, progress, start, end, className }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const targetRef = useRef(progress); targetRef.current = progress;
  const shownRef = useRef(progress);
  const sizesRef = useRef({ start, end }); sizesRef.current = { start, end };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !src) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    let raf = 0;
    let last = performance.now();
    let drawnBlock = -1;
    let drawnW = 0, drawnH = 0;
    let stopped = false;
    const small = document.createElement('canvas');
    const sctx = small.getContext('2d');
    const img = new Image();
    imgRef.current = img;

    const draw = (force: boolean) => {
      const parent = canvas.parentElement;
      if (!parent || !img.complete || !img.naturalWidth || !sctx) return;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const W = Math.max(1, Math.round(parent.clientWidth * dpr));
      const H = Math.max(1, Math.round(parent.clientHeight * dpr));
      const block = mosaicBlock(shownRef.current, sizesRef.current.start, sizesRef.current.end) * dpr;
      if (!force && block === drawnBlock && W === drawnW && H === drawnH) return;
      if (W !== canvas.width || H !== canvas.height) { canvas.width = W; canvas.height = H; }
      drawnBlock = block; drawnW = W; drawnH = H;
      // The picture cover-fitted (as object-fit: cover lays the still), into a canvas one pixel per block.
      const cols = Math.max(1, Math.ceil(W / block)), rows = Math.max(1, Math.ceil(H / block));
      small.width = cols; small.height = rows;
      const iw = img.naturalWidth, ih = img.naturalHeight;
      const scale = Math.max(W / iw, H / ih);
      const sw = W / scale, sh = H / scale;
      const sx = (iw - sw) / 2, sy = (ih - sh) / 2;
      sctx.imageSmoothingEnabled = true;
      sctx.drawImage(img, sx, sy, sw, sh, 0, 0, cols, rows);
      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, W, H);
      ctx.drawImage(small, 0, 0, cols, rows, 0, 0, cols * block, rows * block);
    };

    const tick = (now: number) => {
      if (stopped) return;
      const dt = Math.min(0.25, (now - last) / 1000);
      last = now;
      const gap = targetRef.current - shownRef.current;
      if (Math.abs(gap) > 0.0005) shownRef.current += gap * (1 - Math.exp(-dt / SMOOTH_SEC));
      else shownRef.current = targetRef.current;
      draw(false);
      raf = requestAnimationFrame(tick);
    };

    img.onload = () => { if (!stopped) { draw(true); raf = requestAnimationFrame(tick); } };
    img.src = src;
    if (img.complete && img.naturalWidth) img.onload(new Event('load'));
    const ro = typeof ResizeObserver !== 'undefined' && canvas.parentElement ? new ResizeObserver(() => draw(true)) : null;
    if (ro && canvas.parentElement) ro.observe(canvas.parentElement);
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      ro?.disconnect();
      img.onload = null;
      imgRef.current = null;
    };
  }, [src]);

  if (!src) return null;
  return <canvas ref={canvasRef} className={className ?? 'rstage-mosaic'} aria-hidden="true" />;
};

export default MosaicLayer;
